// lib/revenue/salesBuckets.js — YTD sales bucketing for the Sales-trends chart.
//
// Pure date math over per-day cent totals: no db, no auth, no React. The Sales
// screen builds `dailyTotal` / `dailyDelivery` maps once from salesEntries and
// asks this module for the week / month / quarter view of the year to date,
// each bucket carrying the SAME-PERIOD-LAST-YEAR figure so the chart can draw a
// prior-year reference line on top of the bars.
//
// Alignment rules (why they are what they are):
//  · week    — prior year is the same week shifted back 364 days (52 weeks), NOT
//              the same calendar dates. A restaurant's week is a weekday shape;
//              comparing Mon–Sun to Wed–Tue would be noise, not signal.
//  · month/  — prior year is the same calendar month/quarter one year back.
//    quarter
//  · The last bucket is in progress. Its prior-year twin is truncated to the
//    same elapsed point (`priorCutoff`) so a 3-day-old week is never compared
//    against a full one.
//  · Everything is clipped to Jan 1 (and prior-year Jan 1): this is a
//    year-to-date chart, so the first week never reaches back into December.

/** @param {string} iso @param {number} n @returns {string} */
function shiftIso(iso, n) {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Monday (UTC) of the ISO week containing `iso`. */
function mondayOf(iso) {
  const d = new Date(iso + 'T00:00:00Z');
  const dow = (d.getUTCDay() + 6) % 7; // 0 = Monday
  return shiftIso(iso, -dow);
}

const pad2 = (n) => String(n).padStart(2, '0');

/** Last calendar day of month `m` (1-12) in year `y`, as 'YYYY-MM-DD'. */
function lastDayOfMonth(y, m) {
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}

/**
 * Same day one year earlier, clamped to the shorter month (Feb 29 → Feb 28).
 * @param {string} iso @returns {string}
 */
function sameDayLastYear(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  const end = lastDayOfMonth(y - 1, m);
  const candidate = `${y - 1}-${pad2(m)}-${pad2(d)}`;
  return candidate > end ? end : candidate;
}

/** Inclusive sum of a `{ 'YYYY-MM-DD': cents }` map over [from, to]. */
function sumRange(map, from, to) {
  if (!map || !from || !to || to < from) return 0;
  let s = 0;
  for (let d = from; d <= to; d = shiftIso(d, 1)) s += map[d] || 0;
  return s;
}

const clampLo = (iso, lo) => (iso < lo ? lo : iso);
const clampHi = (iso, hi) => (iso > hi ? hi : iso);

/**
 * Build the current-year (and prior-year) buckets that back the YTD chart.
 *
 * @param {object} opts
 * @param {Record<string, number>} opts.dailyTotal      net cents by 'YYYY-MM-DD'
 * @param {Record<string, number>} [opts.dailyDelivery] delivery-channel subset of the above
 * @param {string} opts.today                           'YYYY-MM-DD'
 * @param {'week'|'month'|'quarter'} opts.granularity
 * @returns {{granularity: string, hasPrior: boolean, firstDataIso: string|null, buckets: Array<object>}}
 */
export function buildYtdBuckets({ dailyTotal = {}, dailyDelivery = {}, today, granularity = 'week' }) {
  const year = Number(today.slice(0, 4));
  const jan1 = `${year}-01-01`;
  const prevJan1 = `${year - 1}-01-01`;
  const prevDec31 = `${year - 1}-12-31`;

  // Is there ANY prior-year sale? With none, the caller hides the overlay
  // rather than drawing a flat zero line that reads like a real collapse.
  const hasPrior = Object.keys(dailyTotal).some(
    (d) => d >= prevJan1 && d <= prevDec31 && (dailyTotal[d] || 0) !== 0,
  );

  // The day the book's records begin. A missing day and a genuine zero-sales
  // day are indistinguishable once summed, so a bucket whose prior-year window
  // reaches back past this is NOT comparable: summing it would score "no
  // records" as "no sales" and manufacture growth. A book that starts in March
  // must not report the first half up 60% because Jan and Feb of last year are
  // simply absent.
  //
  // This assumes records are contiguous from firstDataIso onward, which holds
  // for a bank feed. A hole in the middle of last year would still under-count
  // that bucket; catching that needs a real coverage model, not a start date.
  const days = Object.keys(dailyTotal).sort();
  const firstDataIso = days.length ? days[0] : null;

  // The prior-year twin is the bucket's ACTUAL (already clipped) range shifted
  // back — weeks by 364 days so weekdays line up, calendar buckets by a year.
  // Deriving it from the clipped range is what keeps the comparison honest at
  // both ends of the year: the in-progress bucket compares against the same
  // elapsed days, and the first week of January compares against the same
  // number of days rather than a full week.
  const back = granularity === 'week' ? (iso) => shiftIso(iso, -364) : sameDayLastYear;

  const spans = granularity === 'week' ? weekSpans(today, jan1)
              : granularity === 'month' ? monthSpans(today, year)
              : quarterSpans(today, year);

  const buckets = spans.map((s, i, a) => {
    const start = clampLo(s.start, jan1);
    const end = clampHi(s.end, today);
    const pStart = clampLo(back(start), prevJan1);
    const pEnd = back(end);
    const total = sumRange(dailyTotal, start, end);
    const delivery = sumRange(dailyDelivery, start, end);
    const priorTotal = sumRange(dailyTotal, pStart, pEnd);
    const priorDelivery = sumRange(dailyDelivery, pStart, pEnd);
    return {
      key: s.key,
      startIso: start,
      endIso: end,
      // No display labels here on purpose: month/quarter names have to be
      // formatted in the user's language, so the UI derives every label from
      // startIso + granularity. This module stays pure date math.
      group: s.group,               // colour group: month (weeks) / quarter (months, quarters)
      partial: i === a.length - 1,  // the in-progress bucket
      total, delivery, dineIn: total - delivery,
      priorStartIso: pStart, priorEndIso: pEnd,
      priorTotal, priorDelivery, priorDineIn: priorTotal - priorDelivery,
      hasPriorBucket: pEnd >= pStart,
      // Only these buckets may enter a year-over-year figure: the whole
      // prior-year window sits inside the recorded history, so its total is a
      // real number rather than an absence.
      priorComparable: pEnd >= pStart && firstDataIso != null && pStart >= firstDataIso,
    };
  });

  return { granularity, hasPrior, firstDataIso, buckets };
}

/** ISO weeks (Mon-start) from the week containing Jan 1 through this week. */
function weekSpans(today, jan1) {
  const first = mondayOf(jan1);
  const thisMon = mondayOf(today);
  const out = [];
  for (let m = first; m <= thisMon; m = shiftIso(m, 7)) {
    const start = clampLo(m, jan1);
    out.push({
      key: `w-${m}`,
      start: m, end: shiftIso(m, 6),
      group: Number(start.slice(5, 7)) - 1,
    });
  }
  return out;
}

/** Calendar months Jan → current month. */
function monthSpans(today, year) {
  const cur = Number(today.slice(5, 7));
  const out = [];
  for (let m = 1; m <= cur; m++) {
    out.push({
      key: `m-${year}-${pad2(m)}`,
      start: `${year}-${pad2(m)}-01`, end: lastDayOfMonth(year, m),
      group: Math.floor((m - 1) / 3),
    });
  }
  return out;
}

/** Calendar quarters Q1 → current quarter. */
function quarterSpans(today, year) {
  const curQ = Math.floor((Number(today.slice(5, 7)) - 1) / 3) + 1;
  const out = [];
  for (let q = 1; q <= curQ; q++) {
    const m0 = (q - 1) * 3 + 1;
    out.push({
      key: `q-${year}-${q}`,
      start: `${year}-${pad2(m0)}-01`, end: lastDayOfMonth(year, m0 + 2),
      group: q - 1,
    });
  }
  return out;
}


export const __test = { shiftIso, mondayOf, sameDayLastYear, sumRange };
